import { HistoryPage } from './HistoryPage';
import smoothscroll from 'smoothscroll-polyfill';
import Debounce from '../Utils/Debounce';
smoothscroll.polyfill();

const userDeviceSupportsPassive = () => {
	let result = false;
	try {
		window.addEventListener(
			'test',
			null,
			Object.defineProperty({}, 'passive', {
				get: () => {
					result = true;
					return result;
				},
			})
		);
	} catch (e) {
		console.error(e);
	}
	return result;
};

const wheelOpt = userDeviceSupportsPassive() ? { passive: false } : false;
const wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';

class History {
	static DIR_UP = 'dir-up';
	static DIR_DOWN = 'dir-down';

	static observerOptions = {
		root: null,
		rootMargin: '0px',
		threshold: 0.5,
	};
	static observerCallback(historyPages) {
		historyPages.forEach(historyPage => {
			historyPage.target.classList.toggle('is-in-view', historyPage.isIntersecting);
		});
	}
	static observer = new IntersectionObserver(History.observerCallback, History.observerOptions);

	indexOfPageInView = 0;
	isAtBottom = false;
	isMoving = false;
	timeLineItems = [];
	isMobileUser = false;
	enableNormalScrolling = false;
	disableScrollSnap = false;

	constructor({ mobileSize, rootSelector, pageSelector, timeLineSelector, timeLineItemClass }) {
		this.mobileSize = mobileSize;
		this.root = document.querySelector(rootSelector);
		this.timeLine = this.root.querySelector(timeLineSelector);
		this.timeLineItemClass = timeLineItemClass;
		this.init = this.init.bind(this);
		this.setUp = this.setUp.bind(this);
		this.createPages = this.createPages.bind(this);
		this.clearSetUp = this.clearSetUp.bind(this);
		this.toggleScrollEventListeners = this.toggleScrollEventListeners.bind(this);
		this.handleScroll = this.handleScroll.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.scrollSnap = this.scrollSnap.bind(this);
		this.determinePageToScrollTo = this.findAndScrollToTarget.bind(this);
		this.scrollToPage = this.scrollToPage.bind(this);
		this.scrollToBottom = this.scrollToBottom.bind(this);
		this.toggleTimeLine = this.toggleTimeLine.bind(this);
		this.generateTimeLine = this.generateTimeLine.bind(this);
		this.resize = this.resize.bind(this);

		this._pages = this.createPages(Array.from(this.root.querySelectorAll(pageSelector)));
		this.init();
	}

	createPages(pages) {
		return pages.map(page => {
			return new HistoryPage({
				rootEl: page,
				isFirstPage: page.dataset.isFirstPage,
				cornerContentSelector: '.js-history-page-corner-content',
				year: page?.querySelector('.js-history-page-year')?.dataset?.year,
			});
		});
	}

	init() {
		window.addEventListener('resize', Debounce(this.resize, 250));
		this.setUp();
		this.checkDeeplink();
	}

	resize() {
		this.clearSetUp();
		this.setUp();
	}

	setUp() {
		this.isMobileUser = this.mobileSize > window.innerWidth;
		if (this.isMobileUser) {
			this.pagesWithCornerContent.forEach(page => History.observer.observe(page.root));
			return;
		}

		this.toggleScrollEventListeners('add');
		this.generateTimeLine();

		setTimeout(() => {
			this.scrollToPage(0);
		}, 50);
	}

	checkDeeplink() {
		const hash = window.location.hash;

		if (hash) {
			const targetPage = this._pages.findIndex(
				page => page.root.dataset.cid === hash.replace('#c', '')
			);
			targetPage &&
				setTimeout(() => {
					this.scrollToPage(targetPage);
				}, 250);
		}
	}

	clearSetUp() {
		this.toggleScrollEventListeners('remove');
		this.timeLine.innerHTML = '';
		this.indexOfPageInView = 0;
		this.isAtBottom = false;
		this.isMoving = false;
		this.timeLineItems = [];
		this.disableScrollSnap = false;
		this.enableNormalScrolling = false;

		this.pagesWithCornerContent.forEach(page => {
			History.observer.unobserve(page.root);
		});
	}

	toggleScrollEventListeners(action) {
		window[action + 'EventListener']('DOMMouseScroll', this.handleScroll, false);
		window[action + 'EventListener'](wheelEvent, this.handleScroll, wheelOpt);
		window[action + 'EventListener']('touchmove', this.handleScroll, wheelOpt);
		window[action + 'EventListener']('keydown', this.handleScroll, false);
	}

	generateTimeLine() {
		this.pagesWithCornerContent.forEach((page, index) => {
			const timeLineItem = document.createElement('a');
			timeLineItem.classList.add(this.timeLineItemClass);
			timeLineItem.innerHTML = page.year;

			const handleClick = () => this.scrollToPage(index + 1);
			timeLineItem.addEventListener('click', handleClick);

			this.timeLine.appendChild(timeLineItem);
			this.timeLineItems.push(timeLineItem);
		});
	}

	toggleTimeLine({ show }) {
		this.timeLine.classList.toggle('hidden', !show);
	}

	handleScroll(e) {
		if (this.enableNormalScrolling) return;
		if (e.type === 'keydown') {
			this.handleKeyDown(e);
			return;
		}

		if (!this.disableScrollSnap || this.isAtBottom) this.scrollSnap(e);
	}

	handleKeyDown(e) {
		let scrollDirection = this.getScrollDirectionFromKeyCode(e.keyCode);
		if (!scrollDirection) return;

		e.preventDefault();
		this.findAndScrollToTarget(scrollDirection);
	}

	scrollSnap(e) {
		e.preventDefault();
		if (this.isMoving || (e.type === 'wheel' && Math.abs(e.deltaY) < 30)) return;

		this.isMoving = true;
		setTimeout(() => {
			this.isMoving = false;
			if (!this.isAtBottom) this.scrollToPage();
		}, 600);

		const scrollDirection = e.deltaY > 0 ? History.DIR_DOWN : History.DIR_UP;
		this.findAndScrollToTarget(scrollDirection);
	}

	getScrollDirectionFromKeyCode(keyCode) {
		let scrollDirection = null;
		const scrollUpKeyCodes = [33, 38];
		const scrollDownKeyCodes = [32, 34, 40];

		if (scrollUpKeyCodes.includes(keyCode)) scrollDirection = History.DIR_UP;
		if (scrollDownKeyCodes.includes(keyCode)) scrollDirection = History.DIR_DOWN;
		return scrollDirection;
	}

	findAndScrollToTarget(direction) {
		if (direction === History.DIR_UP) {
			if (this.indexOfPageInView === 0) return;
			if (this.isAtBottom) this.isAtBottom = false;
			else this.indexOfPageInView--;
		}

		if (direction === History.DIR_DOWN) {
			if (this.indexOfPageInView === this._pages.length - 1) return this.scrollToBottom();
			if (this.isAtBottom) return;
			this.indexOfPageInView++;
		}

		this.scrollToPage();
	}

	scrollToPage(index = this.indexOfPageInView) {
		if (index !== this.indexOfPageInView) this.indexOfPageInView = index;

		this.pagesWithCornerContent.forEach((page, idx) => {
			if (index - 1 === idx) return;
			page.toggleIsInView({ inView: false });
			this.timeLineItems[idx].classList.remove('active');
		});

		const page = this._pages[index];
		page.scrollTo();

		if (!page.expandedIsOpen) this.timeLine.classList.remove('hidden');
		this.timeLine.classList.add('is-visible');
		if (index !== 0) {
			this.timeLineItems[index - 1].classList.add('active');
		}
	}

	scrollToBottom() {
		this.timeLine.classList.remove('is-visible');
		this.isAtBottom = true;
		window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
	}

	get pagesWithCornerContent() {
		return this._pages.filter(page => !page.isFirstPage);
	}

	get pageInView() {
		return this._pages[this.indexOfPageInView];
	}
}

new History({
	rootSelector: '.js-history',
	pageSelector: '.js-history-page',
	timeLineSelector: '.js-history-timeline',
	timeLineItemClass: 'history__timeline-item',
	mobileSize: 768,
});
